import { useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import type { MerkTokens } from '../kiosk/useMerkTokens'

/**
 * Hotspot in de scène: een pulserende ring op de plek waar het werk gebeurt.
 * Elke hotspot verschijnt óók als knop in de actiebalk — een raakvlak hoog in
 * het beeld is op een liggend paneel slecht bereikbaar (PLAN.md §2.5.6).
 */
export function Hotspot({
  positie,
  label,
  actief,
  tokens,
  onKies,
}: {
  positie: [number, number, number]
  label: string
  actief: boolean
  tokens: MerkTokens
  onKies: () => void
}) {
  const ring = useRef<THREE.Mesh>(null)
  const kern = useRef<THREE.Mesh>(null)
  const [dichtbij, setDichtbij] = useState(false)

  useFrame((state) => {
    const t = state.clock.elapsedTime
    if (ring.current) {
      const puls = (t % 2.2) / 2.2
      ring.current.scale.setScalar(0.45 + puls * 0.85)
      const m = ring.current.material as THREE.MeshBasicMaterial
      m.opacity = (1 - puls) * (actief ? 0.85 : 0.5)
    }
    if (kern.current) {
      kern.current.scale.setScalar(1 + Math.sin(t * 2.6) * 0.09)
    }
  })

  const kleur = actief ? tokens.brand : tokens.hi

  return (
    <group
      position={positie}
      onClick={(e) => {
        e.stopPropagation()
        onKies()
      }}
      onPointerOver={() => setDichtbij(true)}
      onPointerOut={() => setDichtbij(false)}
    >
      {/* Vergroot, onzichtbaar trefvlak: een ring van 30 cm raak je niet op een kiosk. */}
      <mesh visible={false}>
        <sphereGeometry args={[1.1, 8, 8]} />
        <meshBasicMaterial />
      </mesh>

      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.2, 0]}>
        <ringGeometry args={[0.62, 0.70, 40]} />
        <meshBasicMaterial color={kleur} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
      </mesh>

      <mesh ref={kern}>
        <sphereGeometry args={[0.085, 16, 16]} />
        <meshBasicMaterial color={kleur} toneMapped={false} />
      </mesh>

      {/* Staander naar de vloer, zodat de hotspot in de ruimte staat en niet zweeft. */}
      <mesh position={[0, -0.6, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 1.2, 6]} />
        <meshBasicMaterial color={kleur} transparent opacity={dichtbij || actief ? 0.7 : 0.32} toneMapped={false} />
      </mesh>

      <mesh position={[0, 0.52, 0]} visible={actief}>
        <sphereGeometry args={[0.032, 8, 8]} />
        <meshBasicMaterial color={kleur} toneMapped={false} />
      </mesh>
      <group visible={false} name={label} />
    </group>
  )
}
